import type { SyncTransport } from './sync-transport';
import type { YjsRegistry } from './yjs-registry';
import type { PendingOp, PendingSnapshotOp } from './entity-store';
import {
  encodeEnvelope,
  encodeJson,
  uuidToBytes,
  bytesToUuid,
  type Envelope,
} from './envelope';
import {
  KIND_SUBSCRIBE,
  KIND_UNSUBSCRIBE,
  KIND_OPS,
  KIND_SNAPSHOT_OP,
  KIND_SYNC_REQUEST,
  KIND_YJS_SYNC_STEP1,
  KIND_YJS_SYNC_STEP2,
  KIND_YJS_UPDATE,
  KIND_YJS_AWARENESS,
  KIND_AGENT_WORKFLOWS,
} from './envelope-kinds';
import { CorrelationError } from './correlation-error';
import { setWorkflows, type Workflow } from './workflow-cache';

const CORRELATION_TIMEOUT_MS = 30_000;

/**
 * Heartbeat interval for Cloudflare WS proxy keepalive.
 *
 * Cloudflare idles WebSocket connections at ~100s with no data traffic. The
 * server sends control-frame Pings every 30s, but control frames do NOT
 * reset the proxy's idle timer — only data frames do. We send a tiny
 * client→server data frame every 60s while connected (comfortably under the
 * 100s idle cap) so long-lived WS connections survive behind Cloudflare's
 * orange cloud.
 */
const HEARTBEAT_INTERVAL_MS = 60_000;

interface PendingRequest {
  corrId: string;
  resolve: () => void;
  reject: (err: CorrelationError) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
  createdAt: number;
}

export class SyncEngine {
  /** Map of entityId → entityKind for pending (un-ack'd) subscribes; used for reconnect replay. */
  private pendingSubscribeReplay = new Map<string, string>();
  private unsubscribeStatus: (() => void) | null = null;
  private unsubscribeFrames: (() => void) | null = null;

  // Per-corrId correlation maps. PendingRequest is a resolve/reject pair with a
  // timeout handle; send methods register here on send and the dispatcher calls
  // resolve/reject when the matching server response arrives.
  //
  // Ops correlation (both persistent and ephemeral) lives on opSyncBridge, not
  // here — the bridge is the single owner of the ops corrMap.
  private pendingSync: PendingRequest | null = null;
  private pendingSubscribesByCorrId = new Map<string, PendingRequest>();
  private pendingUnsubscribesByCorrId = new Map<string, PendingRequest>();

  /**
   * Map of corrId → entityId for SUBSCRIBE envelopes emitted by
   * `replaySubscriptions` on reconnect. Those replays are fire-and-forget
   * (no resolve/reject) so they don't get a PendingRequest entry. If the
   * server returns a correlated Error for one of them (e.g. permission
   * revoked while offline), `SyncSystemContext` consults this map via
   * `takeReplayedSubscribeEntityId` to recover the entityId and route the
   * failure through `fireDocAccessDenied`.
   */
  private replayedSubscribesByCorrId = new Map<string, string>();

  /** Heartbeat timer; null when disconnected. See HEARTBEAT_INTERVAL_MS. */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly transport: SyncTransport,
    private readonly registry: YjsRegistry,
  ) {
    this.unsubscribeFrames = transport.onFrame((env) => this.handleIncoming(env));
    this.unsubscribeStatus = transport.onStatus((status) => {
      if (status === 'connected') {
        this.replaySubscriptions();
        this.startHeartbeat();
      }
      if (status === 'disconnected' || status === 'auth-error') {
        this.stopHeartbeat();
        this.onDisconnect();
      }
    });
  }

  dispose(): void {
    this.stopHeartbeat();
    this.unsubscribeStatus?.();
    this.unsubscribeFrames?.();
    this.rejectAllPending(new CorrelationError('connection_lost', undefined, 'engine disposed'));
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      this.transport.sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  sendSubscribe(entityId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const entry = this.registry.getEntry(entityId);
      const entityKind = entry?.kind ?? 'document';
      const corrId = crypto.randomUUID();

      let bytes: Uint8Array;
      try {
        bytes = encodeEnvelope({
          kind: KIND_SUBSCRIBE,
          corrId,
          scopeId: uuidToBytes(entityId),
          json: encodeJson({ entityKind }),
        });
      } catch {
        // Invalid entityId (not a valid UUID) — programmer error, not a runtime
        // failure. Match the original fire-and-forget behavior: log and no-op.
        console.warn(`[SyncEngine] sendSubscribe: invalid entityId "${entityId}"`);
        resolve();
        return;
      }

      if (!this.transport.send(bytes)) {
        // Offline — queue for replay on reconnect. Resolve the promise so
        // fire-and-forget callers don't see a rejection; the reconnect path
        // will issue a fresh SUBSCRIBE with its own corrId and any server
        // rejection will surface through that future request.
        this.pendingSubscribeReplay.set(entityId, entityKind);
        resolve();
        return;
      }

      this.registerPendingSubscribe(corrId, resolve, reject);
    });
  }

  sendUnsubscribe(entityId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.pendingSubscribeReplay.delete(entityId);
      const corrId = crypto.randomUUID();

      let bytes: Uint8Array;
      try {
        bytes = encodeEnvelope({
          kind: KIND_UNSUBSCRIBE,
          corrId,
          scopeId: uuidToBytes(entityId),
        });
      } catch {
        console.warn(`[SyncEngine] sendUnsubscribe: invalid entityId "${entityId}"`);
        resolve();
        return;
      }

      if (!this.transport.send(bytes)) {
        // Offline — nothing to replay (server will clean up connection-scoped
        // state on disconnect anyway). Resolve so callers don't error.
        resolve();
        return;
      }

      this.registerPendingUnsubscribe(corrId, resolve, reject);
    });
  }

  sendLocalUpdate(entityId: string, update: Uint8Array): void {
    try {
      const bytes = encodeEnvelope({
        kind: KIND_YJS_UPDATE,
        scopeId: uuidToBytes(entityId),
        binary: update,
      });
      this.transport.send(bytes);
    } catch {
      // Skip invalid docIds
    }
  }

  sendLocalAwareness(entityId: string, update: Uint8Array): void {
    try {
      const bytes = encodeEnvelope({
        kind: KIND_YJS_AWARENESS,
        scopeId: uuidToBytes(entityId),
        binary: update,
      });
      this.transport.send(bytes);
    } catch {
      // Skip invalid docIds
    }
  }

  clearPending(entityId: string): void {
    this.pendingSubscribeReplay.delete(entityId);
  }

  sendOps(op: PendingOp): { corrId: string; sent: boolean } {
    const corrId = crypto.randomUUID();
    const bytes = encodeEnvelope({
      kind: KIND_OPS,
      corrId,
      json: encodeJson({
        // Wire Protocol C: typed op — spread all op fields and inject opId.
        // The server deserialises as the typed Op enum tagged by "action".
        ops: op.ops.map(o => ({ opId: op.opId, ...o })),
      }),
    });
    const sent = this.transport.send(bytes);
    return { corrId, sent };
  }

  /**
   * Send a snapshot-op batch via the dedicated `KIND_SNAPSHOT_OP` envelope
   * (0x12). Snapshot ops do NOT carry an `entityKind` field — the envelope
   * kind is the discriminator on the server.
   *
   * `binary` is an optional binary payload correlated to a single op via
   * `binaryTarget` (op id). Used today for snapshot create with a
   * client-supplied Yjs state.
   */
  sendSnapshotOps(
    op: PendingSnapshotOp,
    options?: { binaryTarget?: string; binary?: Uint8Array },
  ): { corrId: string; sent: boolean } {
    const corrId = crypto.randomUUID();
    const bytes = encodeEnvelope({
      kind: KIND_SNAPSHOT_OP,
      corrId,
      json: encodeJson({
        ops: op.ops.map(o => ({
          opId: op.opId,
          action: o.action,
          entityId: o.entityId,
          ...(o.fields ? { fields: o.fields } : {}),
        })),
        ...(options?.binaryTarget ? { binaryTarget: options.binaryTarget } : {}),
      }),
      ...(options?.binary ? { binary: options.binary } : {}),
    });
    const sent = this.transport.send(bytes);
    return { corrId, sent };
  }

  sendSync(after: string | null): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const corrId = crypto.randomUUID();
      const bytes = encodeEnvelope({
        kind: KIND_SYNC_REQUEST,
        corrId,
        json: encodeJson({ after }),
      });
      if (!this.transport.send(bytes)) {
        // Offline — nothing is queued for sync; the bootstrap path on the
        // next successful auth will issue a fresh SyncRequest.
        resolve();
        return;
      }
      this.registerPendingSync(corrId, resolve, reject);
    });
  }

  // ── Correlation: resolve / reject / lookup ───────────────────────────────────

  resolveSubscribe(corrId: Uint8Array): void {
    const corrIdStr = bytesToUuid(corrId);
    // Replayed-subscribe entries are fire-and-forget on send; SUBSCRIBE_OK
    // arrival just means the entry is no longer load-bearing — drop it.
    this.replayedSubscribesByCorrId.delete(corrIdStr);
    const pending = this.pendingSubscribesByCorrId.get(corrIdStr);
    if (!pending) return;
    this.pendingSubscribesByCorrId.delete(corrIdStr);
    clearTimeout(pending.timeoutHandle);
    pending.resolve();
  }

  rejectSubscribe(corrId: Uint8Array, serverCode: string, message: string): void {
    const corrIdStr = bytesToUuid(corrId);
    const pending = this.pendingSubscribesByCorrId.get(corrIdStr);
    if (!pending) return;
    this.pendingSubscribesByCorrId.delete(corrIdStr);
    clearTimeout(pending.timeoutHandle);
    pending.reject(new CorrelationError('server_error', serverCode, message));
  }

  resolveUnsubscribe(corrId: Uint8Array): void {
    const corrIdStr = bytesToUuid(corrId);
    const pending = this.pendingUnsubscribesByCorrId.get(corrIdStr);
    if (!pending) return;
    this.pendingUnsubscribesByCorrId.delete(corrIdStr);
    clearTimeout(pending.timeoutHandle);
    pending.resolve();
  }

  rejectUnsubscribe(corrId: Uint8Array, serverCode: string, message: string): void {
    const corrIdStr = bytesToUuid(corrId);
    const pending = this.pendingUnsubscribesByCorrId.get(corrIdStr);
    if (!pending) return;
    this.pendingUnsubscribesByCorrId.delete(corrIdStr);
    clearTimeout(pending.timeoutHandle);
    pending.reject(new CorrelationError('server_error', serverCode, message));
  }

  resolveSync(corrId: Uint8Array): void {
    const corrIdStr = bytesToUuid(corrId);
    if (this.pendingSync?.corrId !== corrIdStr) return;
    const pending = this.pendingSync;
    this.pendingSync = null;
    clearTimeout(pending.timeoutHandle);
    pending.resolve();
  }

  rejectSync(corrId: Uint8Array, serverCode: string, message: string): void {
    const corrIdStr = bytesToUuid(corrId);
    if (this.pendingSync?.corrId !== corrIdStr) return;
    const pending = this.pendingSync;
    this.pendingSync = null;
    clearTimeout(pending.timeoutHandle);
    pending.reject(new CorrelationError('server_error', serverCode, message));
  }

  hasPendingCorrelation(corrId: Uint8Array): boolean {
    const corrIdStr = bytesToUuid(corrId);
    return (
      this.pendingSync?.corrId === corrIdStr ||
      this.pendingSubscribesByCorrId.has(corrIdStr) ||
      this.pendingUnsubscribesByCorrId.has(corrIdStr)
    );
  }

  /**
   * Route a correlated server Error to the pending-request map that owns the corrId.
   * Returns true if the corrId matched a pending request (and was rejected),
   * false if no match was found.
   */
  rejectByCorrId(corrId: Uint8Array, serverCode: string, message: string): boolean {
    const corrIdStr = bytesToUuid(corrId);

    if (this.pendingSync?.corrId === corrIdStr) {
      this.rejectSync(corrId, serverCode, message);
      return true;
    }
    if (this.pendingSubscribesByCorrId.has(corrIdStr)) {
      this.rejectSubscribe(corrId, serverCode, message);
      return true;
    }
    if (this.pendingUnsubscribesByCorrId.has(corrIdStr)) {
      this.rejectUnsubscribe(corrId, serverCode, message);
      return true;
    }
    return false;
  }

  /** Testing hook only — do not use in production code. */
  pendingSubscribesForTest(): string[] {
    return Array.from(this.pendingSubscribeReplay.keys());
  }

  /** Internal helpers used by sendSubscribe/Unsubscribe/Sync in Commit 3. */
  registerPendingSubscribe(corrId: string, resolve: () => void, reject: (err: CorrelationError) => void): void {
    const timeoutHandle = setTimeout(() => {
      if (this.pendingSubscribesByCorrId.has(corrId)) {
        this.pendingSubscribesByCorrId.delete(corrId);
        reject(new CorrelationError('timeout', undefined, `subscribe ${corrId} timed out`));
      }
    }, CORRELATION_TIMEOUT_MS);
    this.pendingSubscribesByCorrId.set(corrId, { corrId, resolve, reject, timeoutHandle, createdAt: Date.now() });
  }

  registerPendingUnsubscribe(corrId: string, resolve: () => void, reject: (err: CorrelationError) => void): void {
    const timeoutHandle = setTimeout(() => {
      if (this.pendingUnsubscribesByCorrId.has(corrId)) {
        this.pendingUnsubscribesByCorrId.delete(corrId);
        reject(new CorrelationError('timeout', undefined, `unsubscribe ${corrId} timed out`));
      }
    }, CORRELATION_TIMEOUT_MS);
    this.pendingUnsubscribesByCorrId.set(corrId, { corrId, resolve, reject, timeoutHandle, createdAt: Date.now() });
  }

  registerPendingSync(corrId: string, resolve: () => void, reject: (err: CorrelationError) => void): void {
    const timeoutHandle = setTimeout(() => {
      if (this.pendingSync?.corrId === corrId) {
        this.pendingSync = null;
        reject(new CorrelationError('timeout', undefined, `sync ${corrId} timed out`));
      }
    }, CORRELATION_TIMEOUT_MS);
    this.pendingSync = { corrId, resolve, reject, timeoutHandle, createdAt: Date.now() };
  }

  private onDisconnect(): void {
    this.rejectAllPending(new CorrelationError('connection_lost', undefined, 'transport disconnected'));
  }

  private rejectAllPending(err: CorrelationError): void {
    if (this.pendingSync) {
      clearTimeout(this.pendingSync.timeoutHandle);
      this.pendingSync.reject(err);
      this.pendingSync = null;
    }
    for (const pending of this.pendingSubscribesByCorrId.values()) {
      clearTimeout(pending.timeoutHandle);
      pending.reject(err);
    }
    this.pendingSubscribesByCorrId.clear();
    for (const pending of this.pendingUnsubscribesByCorrId.values()) {
      clearTimeout(pending.timeoutHandle);
      pending.reject(err);
    }
    this.pendingUnsubscribesByCorrId.clear();
    // Replayed-subscribe corrIds are only valid for the current connection.
    // Drop them on disconnect so stale entries don't route a future error to
    // the wrong entity after reconnect.
    this.replayedSubscribesByCorrId.clear();
  }

  private handleIncoming(envelope: Envelope): void {
    switch (envelope.kind) {
      case KIND_YJS_SYNC_STEP1: {
        const entityId = bytesToUuid(envelope.scopeId);
        const diff = this.registry.encodeStateAsUpdate(entityId, envelope.binary);
        if (diff) {
          const responseBytes = encodeEnvelope({
            kind: KIND_YJS_SYNC_STEP2,
            scopeId: envelope.scopeId,
            binary: diff,
          });
          this.transport.send(responseBytes);
        }
        break;
      }
      case KIND_YJS_SYNC_STEP2:
      case KIND_YJS_UPDATE: {
        const entityId = bytesToUuid(envelope.scopeId);
        this.registry.applyServerUpdate(entityId, envelope.binary);
        this.registry.markServerSynced(entityId);
        break;
      }
      case KIND_YJS_AWARENESS: {
        const entityId = bytesToUuid(envelope.scopeId);
        this.registry.applyServerAwareness(entityId, envelope.binary);
        break;
      }
      case KIND_AGENT_WORKFLOWS: {
        // Server pushes discoverable workflows on WS connect. Cache them
        // in memory so UI components can access them via getWorkflows().
        if (envelope.json.length === 0) break;
        try {
          const body = JSON.parse(new TextDecoder().decode(envelope.json)) as {
            workflows: Workflow[];
          };
          if (Array.isArray(body.workflows)) {
            setWorkflows(body.workflows);
          }
        } catch {
          console.warn('[syncEngine] failed to parse workflows frame');
        }
        break;
      }
      // KIND_AGENT_EVENT and KIND_AGENT_DONE are consumed by AssistantTransport
      // (M12a/M12b) — SyncEngine no longer handles them.
      // All other kinds (auth, ops, sync, error, etc.) are handled by SyncSystemContext
      default:
        break;
    }
  }

  private replaySubscriptions(): void {
    for (const id of this.registry.getAllIds()) {
      const entityKind = this.registry.getEntry(id)?.kind ?? 'document';
      try {
        const corrId = crypto.randomUUID();
        const bytes = encodeEnvelope({
          kind: KIND_SUBSCRIBE,
          corrId,
          scopeId: uuidToBytes(id),
          json: encodeJson({ entityKind }),
        });
        this.transport.send(bytes);
        this.replayedSubscribesByCorrId.set(corrId, id);
      } catch {
        // Skip invalid docIds
      }
    }
    for (const [id, entityKind] of this.pendingSubscribeReplay) {
      try {
        const corrId = crypto.randomUUID();
        const bytes = encodeEnvelope({
          kind: KIND_SUBSCRIBE,
          corrId,
          scopeId: uuidToBytes(id),
          json: encodeJson({ entityKind }),
        });
        this.transport.send(bytes);
        this.replayedSubscribesByCorrId.set(corrId, id);
      } catch {
        // Skip invalid docIds
      }
    }
    this.pendingSubscribeReplay.clear();
    // Re-broadcast local awareness so peers see us immediately after reconnect
    this.registry.rebroadcastAllAwareness();
  }

  /**
   * Recover the entityId for a replayed-subscribe corrId emitted by
   * `replaySubscriptions`. Consumes the entry on hit so a subsequent call
   * with the same corrId returns null. Used by SyncSystemContext on
   * KIND_ERROR to route server errors (e.g. permission revoked) back to
   * fireDocAccessDenied.
   */
  takeReplayedSubscribeEntityId(corrId: Uint8Array): string | null {
    const corrIdStr = bytesToUuid(corrId);
    const entityId = this.replayedSubscribesByCorrId.get(corrIdStr);
    if (entityId === undefined) return null;
    this.replayedSubscribesByCorrId.delete(corrIdStr);
    return entityId;
  }
}
